import type { ActivityModule, ActivityContext, ActivityHandle } from '../../core/activity';
import '../../shared/tens-complement.css';
import levelsData from './levels.json';

interface LevelConfig {
  randomValues: boolean;
  numberOfSubLevels: number;
  numberOfAdditions: number;
  numberOfExtraCards: number;
  minResult: number;
  maxResult: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelConfig[];
}

interface LevelsFile {
  levels: Level[];
}

interface Card {
  id: number;
  value: number;
  hidden: boolean;
}

interface Equation {
  rowIndex: number;
  firstNum: number;
  secondNum: number;
  result: number;
  slot1: number | null;
  slot2: number | null;
  status: 'pending' | 'ok' | 'err';
}

const LEVELS = (levelsData as LevelsFile).levels;

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const tensComplementUse: ActivityModule = {
  id: 'tens_complement_use',
  title: 'Use o complemento de 10',
  description: 'Decomponha somas como 8+7 em (8+2)+5 para calcular usando complementos de 10.',
  ageMin: 7,
  ageMax: 10,
  difficulty: 4,
  iconPath: 'assets/gcompris-AGPL/tens_complement_use/icon.svg',
  category: 'aritmetica',
  totalLevels: LEVELS.length,
  mount,
};

function mount(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const config = level.data[0]!;
  const totalSubs = config.numberOfSubLevels;
  let currentSub = 0;
  let equations: Equation[] = [];
  let pool: Card[] = [];
  let selectedCardId: number | null = null;
  let locked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Use o complemento de 10</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage tens">
      <div class="feedback" aria-live="polite"></div>
      <div class="tens__progress"></div>
      <p class="tens__hint">Para cada soma, complete a decomposição: o primeiro <strong>?</strong> deve somar 10 com o número à esquerda; o segundo <strong>?</strong> é o resto.</p>
      <div class="tens__equations"></div>
      <div class="tens__pool" role="group" aria-label="Cartões disponíveis"></div>
      <div class="tens__actions">
        <button class="btn btn--primary tens__ok" type="button" style="visibility:hidden">OK</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const backBtn = page.querySelector<HTMLButtonElement>('.activity-bar__back')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const equationsEl = page.querySelector<HTMLElement>('.tens__equations')!;
  const poolEl = page.querySelector<HTMLElement>('.tens__pool')!;
  const progressEl = page.querySelector<HTMLElement>('.tens__progress')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.tens__ok')!;

  backBtn.addEventListener('click', () => ctx.onExit());
  okBtn.addEventListener('click', validate);

  initSubLevel();

  function initSubLevel(): void {
    equations = [];
    pool = [];
    selectedCardId = null;
    locked = false;

    const nAdd = Math.min(2, config.numberOfAdditions);
    const cardValues: number[] = [];

    for (let i = 0; i < nAdd; i++) {
      const result = randInt(config.minResult, config.maxResult);
      const firstNum = randInt(1, 9);
      const secondNum = result - firstNum;
      const complement = 10 - firstNum;
      const rest = secondNum - complement;
      cardValues.push(complement, rest);
      equations.push({
        rowIndex: i,
        firstNum,
        secondNum,
        result,
        slot1: null,
        slot2: null,
        status: 'pending',
      });
    }

    let nExtra = config.numberOfExtraCards;
    const maxCards = 6;
    if (cardValues.length + nExtra > maxCards) nExtra = Math.max(0, maxCards - cardValues.length);
    for (let i = 0; i < nExtra; i++) cardValues.push(randInt(1, 9));

    pool = shuffle(cardValues).map((value, id) => ({ id, value, hidden: false }));
    render();
  }

  function render(): void {
    progressEl.textContent = `${currentSub + 1} / ${totalSubs}`;
    equationsEl.innerHTML = '';
    for (const eq of equations) {
      const block = document.createElement('div');
      block.className = `tens__equation tens__equation--${eq.status}`;
      block.style.flexDirection = 'column';
      block.style.alignItems = 'center';
      block.style.gap = 'var(--space-2)';

      const topRow = document.createElement('div');
      topRow.style.display = 'flex';
      topRow.style.alignItems = 'center';
      topRow.style.gap = 'var(--space-2)';
      topRow.appendChild(fixedEl(String(eq.firstNum)));
      topRow.appendChild(opEl('+'));
      topRow.appendChild(fixedEl(String(eq.secondNum)));
      topRow.appendChild(opEl('='));
      topRow.appendChild(fixedEl(String(eq.result)));
      block.appendChild(topRow);

      const botRow = document.createElement('div');
      botRow.style.display = 'flex';
      botRow.style.alignItems = 'center';
      botRow.style.gap = 'var(--space-2)';
      botRow.appendChild(parenEl('('));
      botRow.appendChild(fixedEl(String(eq.firstNum)));
      botRow.appendChild(opEl('+'));
      botRow.appendChild(slotEl(eq.rowIndex, 1, eq.slot1));
      botRow.appendChild(parenEl(')'));
      botRow.appendChild(opEl('+'));
      botRow.appendChild(slotEl(eq.rowIndex, 2, eq.slot2));
      botRow.appendChild(opEl('='));
      botRow.appendChild(fixedEl(String(eq.result)));

      if (eq.status !== 'pending') {
        const mark = document.createElement('span');
        mark.className = 'tens__feedback-mark';
        mark.textContent = eq.status === 'ok' ? '✓' : '✗';
        mark.style.color = eq.status === 'ok' ? 'var(--color-success)' : '#ef4444';
        botRow.appendChild(mark);
      }
      block.appendChild(botRow);
      equationsEl.appendChild(block);
    }
    poolEl.innerHTML = '';
    for (const card of pool) {
      const el = document.createElement('button');
      el.className = 'tens__card';
      if (card.hidden) el.classList.add('tens__card--hidden');
      if (selectedCardId === card.id) el.classList.add('tens__card--selected');
      el.type = 'button';
      el.dataset.cardId = String(card.id);
      el.textContent = String(card.value);
      el.addEventListener('click', () => onCardClick(card.id));
      poolEl.appendChild(el);
    }
    okBtn.style.visibility = allFilled() ? 'visible' : 'hidden';
  }

  function fixedEl(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__fixed';
    el.textContent = text;
    return el;
  }
  function opEl(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__op';
    el.textContent = text;
    return el;
  }
  function parenEl(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__paren';
    el.textContent = text;
    return el;
  }
  function slotEl(rowIndex: number, slotNum: 1 | 2, value: number | null): HTMLElement {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = value !== null ? 'tens__slot tens__slot--filled' : 'tens__slot';
    el.textContent = value !== null ? String(value) : '?';
    el.dataset.row = String(rowIndex);
    el.dataset.slot = String(slotNum);
    el.addEventListener('click', () => onSlotClick(rowIndex, slotNum));
    return el;
  }

  function onCardClick(cardId: number): void {
    if (locked) return;
    const card = pool.find((c) => c.id === cardId);
    if (!card || card.hidden) return;
    selectedCardId = selectedCardId === cardId ? null : cardId;
    ctx.audio.play('click');
    render();
  }

  function onSlotClick(rowIndex: number, slotNum: 1 | 2): void {
    if (locked) return;
    const eq = equations.find((e) => e.rowIndex === rowIndex);
    if (!eq) return;
    const cur = slotNum === 1 ? eq.slot1 : eq.slot2;
    if (cur !== null) {
      const card = pool.find((c) => c.hidden && c.value === cur);
      if (card) card.hidden = false;
      if (slotNum === 1) eq.slot1 = null;
      else eq.slot2 = null;
      eq.status = 'pending';
      ctx.audio.play('click');
      render();
      return;
    }
    if (selectedCardId === null) return;
    const card = pool.find((c) => c.id === selectedCardId);
    if (!card || card.hidden) return;
    if (slotNum === 1) eq.slot1 = card.value;
    else eq.slot2 = card.value;
    card.hidden = true;
    selectedCardId = null;
    ctx.audio.play('click');
    render();
  }

  function allFilled(): boolean {
    return equations.every((e) => e.slot1 !== null && e.slot2 !== null);
  }

  function validate(): void {
    if (locked || !allFilled()) return;
    let allOk = true;
    for (const eq of equations) {
      const tenOk = eq.firstNum + (eq.slot1 ?? 0) === 10;
      const restOk = (eq.slot2 ?? -1) === eq.result - 10;
      const ok = tenOk && restOk;
      eq.status = ok ? 'ok' : 'err';
      if (!ok) allOk = false;
    }
    render();
    if (allOk) {
      locked = true;
      ctx.audio.play('correct');
      showFeedback('✓ Decomposição certa!', 'ok');
      window.setTimeout(() => {
        currentSub++;
        if (currentSub >= totalSubs) finishLevel();
        else initSubLevel();
      }, 1000);
    } else {
      ctx.audio.play('wrong');
      showFeedback('Confira: primeiro par deve somar 10.', 'err');
    }
  }

  function showFeedback(text: string, kind: 'ok' | 'err'): void {
    feedback.textContent = text;
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 1000);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level!.level, totalSubs);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level!.level} concluído!</h2>
      <p class="activity-finish__text">Você completou ${totalSubs} desafios.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level!.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/tens_complement_use/${level!.level + 1}`;
      });
    }
  }

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
